import React, { useEffect, useRef, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, styled, Link, Box, IconButton, Typography, Grid, Button } from '@mui/material';
import { Transactions } from '../../models/dashboard/Transactions';
import { getPdfCredits, getPdfTransactions, getTransactions, getTransactionsDataFilter } from '../../hooks/actions/dashboard';
import { formatDate, formatDateDayMonthYear } from '../../helpers/FormatDate';
import { GRAY_LIGHT, GREEN_DARK, GREEN_LOGO, GREEN_MAIN, WHITE } from '../../theme/colors';
import { formatToTwoDecimals } from '../../helpers/numberFormats';
import { useTranslationContext } from '../../hooks/translations/translationsProvider';
import settings from '../../utilities/settings';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PrintIcon from '@mui/icons-material/Print';
import { getCreditsList } from '../../hooks/actions/credits';
import { CreditData } from '../../models/credits/CreditsModels';

const TableCellDefault = styled(TableCell)(({ theme }) => ({
    backgroundColor: `transparent`, //`${GREEN_LOGO}20`,
    fontWeight:'bold',
    color: GREEN_MAIN,
    border:'0px',
    whiteSpace: 'nowrap',
    paddingBottom:'35px'
}));

const TableCellRowsDefault = styled(TableCell)(({ theme }) => ({
    fontSize:'0.85rem',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    color: GREEN_MAIN,
    borderBottom:'0px',
    padding:'8px 16px'
}));

const CustomTableContainer = styled(TableContainer)(({ theme }) => ({
    // Base scrollbar styling
    "&::-webkit-scrollbar": {
      height: "8px", // Height for horizontal scrollbar
      width: "8px", // Width for vertical scrollbar
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.mode === "light" ? "#f1f1f1" : "#2d2d2d",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    // This is the key part - remove ALL scrollbar buttons/arrows
    "&::-webkit-scrollbar-button": {
      display: "none !important",
      width: "0px",
      height: "0px",
    },
    // For Firefox
    scrollbarWidth: "thin",
    scrollbarColor: `${GREEN_LOGO} ${theme.palette.mode === "light" ? "#f1f1f1" : "#2d2d2d"}`,
    // For IE and Edge
    msOverflowStyle: "none",
  
    // Ensure horizontal scrolling is enabled
    overflowX: "auto",
    maxWidth: "100%",
  }))


const CreditsTable: React.FC = () => {
    const [credits, setCredits] = useState<CreditData>();
    const [last, setLast] = useState<boolean>(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const {t} = useTranslationContext();

    const fetchTransactions = () => {
        getCreditsList(page, rowsPerPage, last)
            .then(response => {
                setCredits(response);
                setTotalRecords(response.totalRecords);
            })
            .catch(e => {
                console.log(e)
            });
    };

    useEffect(() => {     
        fetchTransactions();
    }, [page, rowsPerPage, last]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box sx={{width:'100%', overflow: 'hidden', maxWidth: '100%', fontFamily:'poppins'}}>
            <Paper sx={{ 
                width: '100%', 
                overflow: 'hidden', 
                // boxShadow:5, 
                borderColor: 'transparent',
                maxWidth: '100%', // Limita la larghezza massima del Paper
                boxSizing: 'border-box',
                }}>
                <Box
                    sx={{
                        paddingLeft:'16px'
                    }}
                >
                    <Typography sx={{ display: 'flex', gap: 1, fontSize:'0.8rem', color: GREEN_LOGO, fontStyle:'italic', fontWeight:'normal' }}>
                        (<span
                            style={{ 
                                cursor: 'pointer', 
                                textDecoration: last === true ? 'underline' : 'none',
                                marginLeft: '-7px'
                            }}
                            onClick={() => setLast(true)}
                        >
                            {t("ultimi 6 mesi")}
                        </span>-<span
                            style={{ 
                                cursor: 'pointer', 
                                textDecoration: last === false ? 'underline' : 'none',
                                marginRight: '-7px'
                            }}
                            onClick={() => setLast(false)}
                        >
                            {t("tutto")}
                        </span>)
                    </Typography>
                </Box>
                <CustomTableContainer>
                    <Table aria-label="transactions table"> {/** stickyHeader */}
                        <TableHead>
                            <TableRow sx={{ 
                            }}>
                                <TableCellDefault>{t("Data")}</TableCellDefault>
                                <TableCellDefault>{t("Operazione")}</TableCellDefault>
                                <TableCellDefault
                                    sx={{
                                        textAlign:'center'
                                    }}
                                >{t("Punti fedeltà")}</TableCellDefault>
                                <TableCellDefault
                                    sx={{
                                        textAlign:'center'
                                    }}
                                >{t("Bonus")}</TableCellDefault>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {credits && credits.list.map((credit) => (
                                <TableRow>
                                    <TableCellRowsDefault>{ formatDateDayMonthYear(credit.dataConfirmed) }</TableCellRowsDefault>
                                    <TableCellRowsDefault>{ t("CREDIT-"+credit.creditType.toString()) }</TableCellRowsDefault>
                                    <TableCellRowsDefault
                                        sx={{
                                            justifyItems: 'center',
                                        }}
                                    >
                                        <Box sx={{ 
                                            textAlign: 'center',
                                            // width: '100px'    
                                        }}>
                                            { formatToTwoDecimals(credit.credits) }
                                        </Box>
                                    </TableCellRowsDefault>
                                    <TableCellRowsDefault
                                        sx={{
                                            justifyItems: 'center',
                                        }}
                                    >
                                        <Box sx={{ 
                                            textAlign: credit.tokens == null || credit.tokens === 0 ? 'center' : 'center',
                                            // width: '100px'
                                        }}>
                                            { credit.tokens == null || credit.tokens === 0 ? "-" : formatToTwoDecimals(credit.tokens) }
                                        </Box>
                                    </TableCellRowsDefault>
                                </TableRow>
                            ))}
                                <TableRow>
                                    <TableCellRowsDefault></TableCellRowsDefault>
                                    <TableCellRowsDefault sx={{
                                        fontSize:'1rem',
                                        fontWeight: 'Bold',
                                        paddingTop:'25px'
                                    }}>{ t("TOTALE") }</TableCellRowsDefault>
                                    <TableCellRowsDefault
                                        sx={{
                                            justifyItems: 'center',
                                            fontSize:'1rem',
                                            fontWeight: 'Bold'
                                        }}
                                    >
                                        <Box sx={{ 
                                            textAlign: 'center',
                                            // width: '100px'    
                                        }}>
                                            { formatToTwoDecimals(credits?.totalHistoricalCredits ?? 0) }
                                        </Box>
                                    </TableCellRowsDefault>
                                    <TableCellRowsDefault
                                        sx={{
                                            justifyItems: 'center',
                                            fontSize:'1rem',
                                            fontWeight: 'Bold'
                                        }}
                                    >
                                        <Box sx={{ 
                                            textAlign: 'center',
                                        }}>
                                            { formatToTwoDecimals(credits?.totalBonusTokens ?? 0) }
                                        </Box>
                                    </TableCellRowsDefault>
                                </TableRow>
                        </TableBody>
                    </Table>
                </CustomTableContainer>
                <TablePagination
                    labelRowsPerPage={t("Risultati per pagina")}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t("di")} ${count}`}
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={totalRecords}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        color:GREEN_MAIN,
                        "& .MuiTablePagination-select": {
                            "&:focus": {
                              backgroundColor: "transparent",
                            },
                        },
                        // This targets the dropdown arrow icon
                        "& .MuiTablePagination-selectIcon": {
                            color: GREEN_MAIN,
                        },
                        // This targets the navigation arrows
                        "& .MuiTablePagination-actions": {
                            "& .MuiIconButton-root": {
                            color: GREEN_MAIN,
                            },
                        },
                        // Optional: Style the text to match
                        "& .MuiTablePagination-displayedRows": {
                            color: GREEN_MAIN,
                        },
                        "& .MuiTablePagination-selectLabel": {
                            color: GREEN_MAIN,
                        },
                    }}
                />

                <Grid container justifyContent={'center'} padding={'30px 0px 35px 0px'}>
                    <Grid item > {/** xs={8} sm={4} */}
                        <Button type="submit" variant="contained" onClick={() => {getPdfCredits().finally()}}
                            sx={{
                                bgcolor: GREEN_LOGO,
                                "&:hover": { bgcolor: "darkgreen" },
                                color:WHITE,
                                border: "1px solid " + GREEN_LOGO,
                                fontSize:'0.8rem',
                                textTransform: "none",
                                letterSpacing:'0.1em'
                            }}>
                            {t("Esporta Punti Fedeltà")}
                        </Button>
                    </Grid>
                </Grid>

            </Paper>
        </Box>
    );
};

export default CreditsTable;